import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '../api';
import './Register.css';
import logo from '../assets/logo.png';

// ===== validators =====
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
const isCNMobile = (v) => /^1[3-9]\d{9}$/.test((v || '').trim());
const isE164 = (v) => /^\+?[1-9]\d{7,14}$/.test((v || '').trim());
const phoneValidStrict = (v) => {
  const s = (v || '').trim();
  if (!s) return false; // 必填
  return s.startsWith('+') ? isE164(s) : isCNMobile(s);
};
const isPassword = (v) => /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/.test(v || '');
// =======================

export default function Register() {
  const [nickname, setNickname] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({
    nickname: false, phone: false, email: false, password: false, confirm: false
  });

  const nav = useNavigate();

  const nicknameValid = useMemo(() => (nickname || '').trim().length > 0, [nickname]);
  const phoneValid = useMemo(() => phoneValidStrict(phone), [phone]);
  const emailValid = useMemo(() => isEmail(email), [email]);
  const passwordValid = useMemo(() => isPassword(password), [password]);
  const confirmValid = useMemo(() => confirm === password && passwordValid, [confirm, passwordValid, password]);

  // 错误文案
  const nicknameError = !nicknameValid ? '请输入昵称' : '';
  const phoneError = (() => {
    const s = (phone || '').trim();
    if (!s) return '请输入手机号';
    return phoneValid ? '' : '手机号格式不正确（+国家码为国际号码，否则按中国大陆号段）';
  })();
  const emailError = !emailValid ? '请输入有效的邮箱地址' : '';
  const passwordError = !passwordValid ? '密码需包含“字母+数字”，且至少6位' : '';
  const confirmError = !confirmValid ? '两次输入的密码不一致' : '';

  const canSubmit = nicknameValid && phoneValid && emailValid && passwordValid && confirmValid && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({ nickname: true, phone: true, email: true, password: true, confirm: true });
    if (!canSubmit) {
      const firstErr = nicknameError || phoneError || emailError || passwordError || confirmError || '请检查表单字段';
      setErr(firstErr);
      return;
    }
    setErr('');
    try {
      setSubmitting(true);
      const data = await apiFetch('/api/auth/register', {
        method: 'POST',
        body: {
          nickname: nickname.trim(),
          phone: phone.trim(),
          email: email.trim(),
          password,
        },
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('nickname', data.user?.nickname || '');
      nav('/');
    } catch (e) {
      setErr(e?.message || '注册失败，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  };

  const markTouched = (key) => setTouched((t) => ({ ...t, [key]: true }));

  return (
    <main className="register-page">
      <div className="bg-dots" aria-hidden="true"></div>
      <section className="card">
        <header className="card-header">
          <img src={logo} alt="logo" className="logo-img" />
          <div>
            <h1 className="title">创建你的账户</h1>
            <p className="subtitle">欢迎加入本源AI · 简单高效</p>
          </div>
        </header>

        <form onSubmit={onSubmit} className="form" noValidate>
          {/* 昵称 */}
          <div className="form-row">
            <label className="label" htmlFor="nickname">昵称 <span className="req">*</span></label>
            <input
              id="nickname"
              className={`input ${touched.nickname && !nicknameValid ? 'invalid' : ''}`}
              placeholder="给自己取个名字"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              onBlur={() => markTouched('nickname')}
              required
              aria-invalid={(touched.nickname && !nicknameValid).toString()}
              aria-describedby="nicknameErr"
            />
            {touched.nickname && !nicknameValid && (
              <div id="nicknameErr" className="field-error" role="alert">{nicknameError}</div>
            )}
          </div>

          {/* 手机号 */}
          <div className="form-row">
            <label className="label" htmlFor="phone">手机号 <span className="req">*</span></label>
            <input
              id="phone"
              className={`input ${touched.phone && !phoneValid ? 'invalid' : ''}`}
              placeholder="中国大陆：11位；或 +国家码的国际号码"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => markTouched('phone')}
              required
              aria-invalid={(touched.phone && !phoneValid).toString()}
              aria-describedby="phoneHelp phoneErr"
            />
            <small id="phoneHelp" className="hint">
              以 “+” 开头按国际标准校验；否则按中国大陆号段校验
            </small>
            {touched.phone && !phoneValid && (
              <div id="phoneErr" className="field-error" role="alert">{phoneError}</div>
            )}
          </div>

          {/* 邮箱 */}
          <div className="form-row">
            <label className="label" htmlFor="email">邮箱 <span className="req">*</span></label>
            <input
              id="email"
              type="email"
              className={`input ${touched.email && !emailValid ? 'invalid' : ''}`}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => markTouched('email')}
              required
              aria-invalid={(touched.email && !emailValid).toString()}
              aria-describedby="emailHelp emailErr"
            />
            <small id="emailHelp" className="hint">将用于登录与找回密码</small>
            {touched.email && !emailValid && (
              <div id="emailErr" className="field-error" role="alert">{emailError}</div>
            )}
          </div>

          {/* 密码 */}
          <div className="form-row">
            <label className="label" htmlFor="password">密码 <span className="req">*</span></label>
            <input
              id="password"
              type="password"
              className={`input ${touched.password && !passwordValid ? 'invalid' : ''}`}
              placeholder="至少 6 位，需包含字母与数字"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => markTouched('password')}
              required
              aria-invalid={(touched.password && !passwordValid).toString()}
              aria-describedby="pwdHelp pwdErr"
            />
            <small id="pwdHelp" className="hint">
              必须包含字母与数字，长度 ≥ 6
            </small>
            {touched.password && !passwordValid && (
              <div id="pwdErr" className="field-error" role="alert">{passwordError}</div>
            )}
          </div>

          {/* 确认密码 */}
          <div className="form-row">
            <label className="label" htmlFor="confirm">确认密码 <span className="req">*</span></label>
            <input
              id="confirm"
              type="password"
              className={`input ${touched.confirm && !confirmValid ? 'invalid' : ''}`}
              placeholder="再次输入密码"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onBlur={() => markTouched('confirm')}
              required
              aria-invalid={(touched.confirm && !confirmValid).toString()}
              aria-describedby="confirmErr"
            />
            {touched.confirm && !confirmValid && (
              <div id="confirmErr" className="field-error" role="alert">{confirmError}</div>
            )}
          </div>

          {err && <p className="error" role="alert">{err}</p>}

          <div className="form-actions">
            <button
              type="submit"
              className="primary-btn"
              disabled={!canSubmit}
              aria-disabled={!canSubmit}
            >
              {submitting ? '注册中…' : '注册'}
            </button>
            <span className="alt-inline">
              已有账号？<Link to="/login" className="link">去登录</Link>
            </span>
          </div>
        </form>
      </section>
    </main>
  );
}
